import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, LogOut, ExternalLink } from "lucide-react";
import { api, formatPrice, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const CATEGORIES = ["Sofas", "Beds", "Dining Tables", "Chairs", "Wardrobes", "Office"];
const EMPTY = { name: "", category: "Sofas", price: "", short_desc: "", description: "", material: "", dimensions: "", image: "", featured: false, in_stock: true };

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = useCallback(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/admin/inquiries").then((r) => setInquiries(r.data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setDialogOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({ ...EMPTY, ...p, price: String(p.price) });
    setDialogOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { ...form, price: parseFloat(form.price) || 0, gallery: [] };
    try {
      if (editing) {
        await api.put(`/admin/products/${editing.id}`, payload);
        toast.success("Product updated");
      } else {
        await api.post("/admin/products", payload);
        toast.success("Product created");
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/admin/products/${deleteTarget.id}`);
      toast.success("Product deleted");
      setDeleteTarget(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const deleteInquiry = async (id) => {
    try {
      await api.delete(`/admin/inquiries/${id}`);
      setInquiries((prev) => prev.filter((i) => i.id !== id));
      toast.success("Inquiry removed");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const handleLogout = () => { logout(); navigate("/admin/login"); };

  return (
    <div className="min-h-screen bg-background" data-testid="admin-dashboard">
      <header className="border-b border-border sticky top-0 bg-background/90 backdrop-blur-md z-40">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <div>
            <span className="font-serif text-xl">Gulhar</span>
            <span className="text-[10px] tracking-[0.3em] uppercase text-primary ml-2">Admin</span>
          </div>
          <div className="flex items-center gap-4">
            <a href="/" target="_blank" rel="noreferrer" data-testid="view-site" className="text-sm text-muted-foreground hover:text-primary inline-flex items-center gap-2"><ExternalLink size={15} /> View site</a>
            <span className="text-sm text-muted-foreground hidden sm:inline">{user?.email}</span>
            <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="logout-btn" className="text-foreground hover:text-primary"><LogOut size={16} className="mr-1" /> Logout</Button>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-10">
        <Tabs defaultValue="products">
          <TabsList className="bg-secondary rounded-none">
            <TabsTrigger value="products" data-testid="tab-products" className="rounded-none">Products ({products.length})</TabsTrigger>
            <TabsTrigger value="inquiries" data-testid="tab-inquiries" className="rounded-none">Enquiries ({inquiries.length})</TabsTrigger>
          </TabsList>

          {/* PRODUCTS */}
          <TabsContent value="products" className="mt-8">
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-serif text-3xl font-light">Products</h2>
              <Button onClick={openCreate} data-testid="add-product-btn" className="bg-primary text-primary-foreground hover:opacity-90 rounded-none"><Plus size={16} className="mr-1" /> Add Product</Button>
            </div>
            <div className="border border-border">
              <Table>
                <TableHeader>
                  <TableRow className="border-border hover:bg-transparent">
                    <TableHead>Image</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Price</TableHead>
                    <TableHead>Featured</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {products.map((p) => (
                    <TableRow key={p.id} className="border-border" data-testid={`row-${p.id}`}>
                      <TableCell><img src={p.image} alt={p.name} className="h-12 w-12 object-cover border border-border" /></TableCell>
                      <TableCell className="font-medium">{p.name}</TableCell>
                      <TableCell className="text-muted-foreground">{p.category}</TableCell>
                      <TableCell className="text-primary">{formatPrice(p.price)}</TableCell>
                      <TableCell>{p.featured ? "Yes" : "—"}</TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="icon" onClick={() => openEdit(p)} data-testid={`edit-${p.id}`}><Pencil size={15} /></Button>
                        <Button variant="ghost" size="icon" onClick={() => setDeleteTarget(p)} data-testid={`delete-${p.id}`} className="text-destructive"><Trash2 size={15} /></Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* INQUIRIES */}
          <TabsContent value="inquiries" className="mt-8">
            <h2 className="font-serif text-3xl font-light mb-6">Enquiries</h2>
            {inquiries.length === 0 ? (
              <p className="text-muted-foreground py-16 text-center border border-border" data-testid="no-inquiries">No enquiries yet.</p>
            ) : (
              <div className="grid md:grid-cols-2 gap-5">
                {inquiries.map((iq) => (
                  <div key={iq.id} className="border border-border bg-card p-6" data-testid={`inquiry-${iq.id}`}>
                    <div className="flex justify-between items-start gap-4">
                      <div>
                        <p className="font-medium">{iq.name}</p>
                        <p className="text-sm text-muted-foreground">{iq.email}{iq.phone ? ` · ${iq.phone}` : ""}</p>
                      </div>
                      <Button variant="ghost" size="icon" onClick={() => deleteInquiry(iq.id)} data-testid={`delete-inquiry-${iq.id}`} className="text-destructive"><Trash2 size={15} /></Button>
                    </div>
                    {iq.product_name && <p className="text-xs text-primary mt-3 tracking-wide uppercase">Re: {iq.product_name}</p>}
                    <p className="text-sm text-foreground/90 mt-3 leading-relaxed">{iq.message}</p>
                    <p className="text-xs text-muted-foreground mt-4">{new Date(iq.created_at).toLocaleString()}</p>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Product Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-card border-border max-w-2xl max-h-[90vh] overflow-y-auto rounded-none" data-testid="product-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl font-light">{editing ? "Edit Product" : "New Product"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4 mt-2">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Name"><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="form-name" className="bg-background border-border" /></Field>
              <Field label="Category">
                <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="form-category" className="w-full h-10 bg-background border border-border px-3 text-sm">
                  {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </Field>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Price (INR)"><Input required type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="form-price" className="bg-background border-border" /></Field>
              <Field label="Image URL"><Input required value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} data-testid="form-image" className="bg-background border-border" /></Field>
            </div>
            <Field label="Short description"><Input value={form.short_desc} onChange={(e) => setForm({ ...form, short_desc: e.target.value })} data-testid="form-short" className="bg-background border-border" /></Field>
            <Field label="Description"><Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="form-desc" className="bg-background border-border" /></Field>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Material"><Input value={form.material} onChange={(e) => setForm({ ...form, material: e.target.value })} data-testid="form-material" className="bg-background border-border" /></Field>
              <Field label="Dimensions"><Input value={form.dimensions} onChange={(e) => setForm({ ...form, dimensions: e.target.value })} data-testid="form-dimensions" className="bg-background border-border" /></Field>
            </div>
            <div className="flex items-center gap-3 pt-2">
              <Switch checked={form.featured} onCheckedChange={(v) => setForm({ ...form, featured: v })} data-testid="form-featured" />
              <Label className="text-sm">Feature on homepage</Label>
            </div>
            <DialogFooter className="pt-4">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-none border-border">Cancel</Button>
              <Button type="submit" disabled={saving} data-testid="save-product-btn" className="bg-primary text-primary-foreground hover:opacity-90 rounded-none">{saving ? "Saving…" : "Save"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="bg-card border-border rounded-none" data-testid="delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif font-light">Delete “{deleteTarget?.name}”?</AlertDialogTitle>
            <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none border-border">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} data-testid="confirm-delete" className="bg-destructive text-destructive-foreground rounded-none hover:opacity-90">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-2">
      <Label className="text-xs tracking-wide uppercase text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}
